import { HubOutlinedIcon } from "@storytell/bsky-icons";
import type { StreamingOperation } from "@storytell/streaming";
import { TbBooks, TbRoute, TbSearch, TbWorld } from "solid-icons/tb";
import type { Component } from "solid-js";

interface AgentIdentity {
  label: string;
  role: string;
  labelKey?: string;
  roleKey?: string;
  badgeColor: string;
  iconBgColor: string;
  Icon: Component<{ class?: string }>;
}

const AGENT_IDENTITIES: Record<string, AgentIdentity> = {
  explorer: {
    label: "Explorer",
    role: "Web Research",
    labelKey: "agent.explorer.label",
    roleKey: "agent.explorer.role",
    badgeColor: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
    iconBgColor: "bg-purple-600 dark:bg-purple-500",
    Icon: TbWorld,
  },
  plan: {
    label: "Planner",
    role: "Strategy & Planning",
    labelKey: "agent.planner.label",
    roleKey: "agent.planner.role",
    badgeColor: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    iconBgColor: "bg-amber-600 dark:bg-amber-500",
    Icon: TbRoute,
  },
  librarian: {
    label: "Librarian",
    role: "Knowledge Base",
    labelKey: "agent.librarian.label",
    roleKey: "agent.librarian.role",
    badgeColor: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
    iconBgColor: "bg-orange-600 dark:bg-orange-500",
    Icon: TbBooks,
  },
  researcher: {
    label: "Researcher",
    role: "Deep Analysis",
    labelKey: "agent.researcher.label",
    roleKey: "agent.researcher.role",
    badgeColor: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300",
    iconBgColor: "bg-cyan-600 dark:bg-cyan-500",
    Icon: TbSearch,
  },
};

const BADGE_COLORS = [
  "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
  "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300",
  "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
  "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
  "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300",
  "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300",
  "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300",
];

const ICON_BG_COLORS = [
  "bg-purple-600 dark:bg-purple-500",
  "bg-amber-600 dark:bg-amber-500",
  "bg-cyan-600 dark:bg-cyan-500",
  "bg-orange-600 dark:bg-orange-500",
  "bg-emerald-600 dark:bg-emerald-500",
  "bg-rose-600 dark:bg-rose-500",
  "bg-indigo-600 dark:bg-indigo-500",
  "bg-teal-600 dark:bg-teal-500",
];

export function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function capitalize(s: string): string {
  return s.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Generic over the key so each app can hand over its own typed translator.
type Translator<K extends string> = (key: K) => string;

export function resolveAgentLabel<K extends string>(identity: AgentIdentity, t: Translator<K>): string {
  return identity.labelKey ? t(identity.labelKey as K) : identity.label;
}

export function resolveAgentRole<K extends string>(identity: AgentIdentity, t: Translator<K>): string {
  return identity.roleKey ? t(identity.roleKey as K) : identity.role;
}

export function extractAgentType(operation: StreamingOperation): string {
  if (operation.name?.startsWith("Agent:")) return operation.name.replace("Agent:", "").trim();
  if (operation.activity?.startsWith("agent_toolu_")) return operation.activity.replace("agent_toolu_", "");
  return "default";
}

/** The title an agent's card shows. */
export function resolveAgentDisplayName<K extends string>(
  operation: StreamingOperation,
  agentType: string,
  t: Translator<K>
): string {
  if (operation.task?.taskName) return operation.task.taskName;
  if (operation.name && !operation.name.startsWith("Agent:")) return operation.name;
  return resolveAgentLabel(getAgentIdentity(agentType), t);
}

export function getAgentIdentity(agentType: string): AgentIdentity {
  const known = AGENT_IDENTITIES[agentType];
  if (known) return known;

  const idx = hashString(agentType) % BADGE_COLORS.length;
  const badgeColor = BADGE_COLORS[idx];
  const iconBgColor = ICON_BG_COLORS[idx];
  if (badgeColor === undefined || iconBgColor === undefined) {
    throw new Error(`agent palettes differ in length: no entry at ${idx}`);
  }
  return {
    label: capitalize(agentType),
    role: capitalize(agentType),
    badgeColor,
    iconBgColor,
    Icon: HubOutlinedIcon,
  };
}
